
import { useState } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Home, Settings, Ruler, Check, Star, Shield, Award } from "lucide-react";
import CanonicalTag from "@/components/CanonicalTag";

const CanIBuildADUBayArea = () => {
  return <div className="min-h-screen bg-white">
      <CanonicalTag url="https://www.udaadu.com/can-i-build-adu-bay-area" />
      <Navigation />
      <FloatingADUWizard />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Can I Build an ADU on My Property in the{" "}
              <span className="text-[#E4683B]">Bay Area?</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Discover your personalized ADU options—size, style, regulations, and pricing—for your exact address in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/landing">
                <Button size="lg" className="bg-[#E4683B] hover:bg-[#E4683B]/80 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  Start My Free ADU Scan
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#E4683B] mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get your personalized ADU feasibility report in three simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="text-center p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-[#E4683B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Home className="w-8 h-8 text-[#E4683B]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Step 1: Enter Your Address
                </h3>
                <p className="text-gray-600">
                  Simply enter your Bay Area property address to get started with your personalized ADU analysis.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-[#E4683B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Settings className="w-8 h-8 text-[#E4683B]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Step 2: Run ADU Feasibility Check
                </h3>
                <p className="text-gray-600">
                  Our system analyzes local zoning laws, setback requirements, and lot constraints for your specific location.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-[#E4683B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Ruler className="w-8 h-8 text-[#E4683B]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Step 3: See ADU Models & Costs
                </h3>
                <p className="text-gray-600">
                  View tailored ADU designs and transparent cost estimates based on your lot size and local regulations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 bg-black">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#E4683B]">
              Why Use Our ADU Wizard?
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-white">
              Get comprehensive insights about your ADU potential with our advanced analysis tool
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-500">
                  Instant Lot Eligibility & Size Limits
                </h3>
                <p className="text-white">
                  Know immediately if your property qualifies for an ADU and what size constraints apply to your specific lot.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-500">
                  Tailored ADU Designs
                </h3>
                <p className="text-white">
                  Explore studio to 2-bedroom ADU options designed specifically for your lot size and local requirements.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-500">
                  Bay Area Zoning Compliance
                </h3>
                <p className="text-white">
                  Ensure your ADU plans meet all Bay Area zoning laws and setback rules before you start building.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-500">
                  Transparent Cost Estimates
                </h3>
                <p className="text-white">
                  Get accurate pricing based on your location, desired size, and current market conditions in the Bay Area.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-500">
                  Anonymous & Private
                </h3>
                <p className="text-white">
                  No personal follow-ups or sales calls. Get your ADU analysis completely anonymously and privately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 md:text-4xl text-[#E4683B]">
              Trusted by Bay Area Homeowners
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-white">
              See what homeowners are saying about their ADU journey
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            <Card className="p-6 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-gray-600 mb-4">
                  "I found out I could build a 1,200 sq ft ADU on my San Jose property. The wizard made it so easy to understand the regulations and costs."
                </p>
                <div className="font-semibold text-gray-900">Sarah M.</div>
                <div className="text-sm text-gray-500">San Jose Homeowner</div>
              </CardContent>
            </Card>

            <Card className="p-6 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-gray-600 mb-4">
                  "The feasibility check saved me months of research. I knew exactly what I could build before talking to contractors."
                </p>
                <div className="font-semibold text-gray-900">David L.</div>
                <div className="text-sm text-gray-500">Palo Alto Homeowner</div>
              </CardContent>
            </Card>

            <Card className="p-6 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-gray-600 mb-4">
                  "Perfect for understanding setback requirements in Mountain View. The cost estimates were spot-on with actual quotes."
                </p>
                <div className="font-semibold text-gray-900">Maria R.</div>
                <div className="text-sm text-gray-500">Mountain View Homeowner</div>
              </CardContent>
            </Card>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-70">
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Licensed in CA</span>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="w-6 h-6 text-green-600" />
              <span className="text-sm font-medium text-gray-600">BBB Accredited</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-6 h-6 text-yellow-500" />
              <span className="font-medium text-[#e4683b] text-4xl">5-Star Rated</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 text-[#E4683B] md:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-white">
              Get answers to the most common ADU questions for Bay Area properties
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white rounded-lg shadow-sm border">
                <AccordionTrigger className="px-6 py-4 text-left">
                  What ADU sizes are allowed in San Jose / Bay Area?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  ADU sizes vary by city and lot size in the Bay Area. Generally, detached ADUs can be up to 1,200 sq ft, while attached ADUs may be limited to 50% of the primary home's size. Our wizard analyzes your specific city's regulations and lot constraints to show you exactly what's allowed on your property.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-lg shadow-sm border">
                <AccordionTrigger className="px-6 py-4 text-left">
                  Are two-story ADUs allowed on my lot?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  Two-story ADUs are allowed in most Bay Area cities, subject to height restrictions (typically 16-25 feet) and setback requirements. The feasibility depends on your lot size, existing structures, and local zoning. Our analysis will show you if a two-story design fits your property's constraints.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-lg shadow-sm border">
                <AccordionTrigger className="px-6 py-4 text-left">
                  How much does a typical Bay Area ADU cost per sq ft?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  Bay Area ADU costs typically range from $300-$600 per square foot, depending on finishes, complexity, and location. Factors include site conditions, utility connections, permitting costs, and labor rates. Our cost estimator provides location-specific pricing based on current market conditions in your area.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-lg shadow-sm border">
                <AccordionTrigger className="px-6 py-4 text-left">
                  Do I need special permits for an ADU in the Bay Area?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  Yes, ADUs require building permits in all Bay Area cities. However, many jurisdictions now offer streamlined permitting processes for ADUs that meet standard requirements. Our wizard helps you understand the specific permit requirements and timelines for your city.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white rounded-lg shadow-sm border">
                <AccordionTrigger className="px-6 py-4 text-left">
                  Can I rent out my ADU for income?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  Yes, ADUs can be rented out in most Bay Area cities, providing an excellent source of rental income. Some cities have specific rental requirements or deed restrictions. Our analysis includes information about rental potential and any local restrictions that may apply to your property.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#E4683B] mb-6">
            Ready to Discover Your ADU Potential?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Start your free, personalized ADU scan and see what's possible on your Bay Area property.
          </p>
          <Link to="/landing">
            <Button size="lg" className="bg-white text-[#E4683B] hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              Find Out Now
            </Button>
          </Link>
          <p className="text-sm text-white/80 mt-4">
            Start my free, personalized ADU scan
          </p>
        </div>
      </section>

      <Footer />
    </div>;
};
export default CanIBuildADUBayArea;
